import React, { useEffect, useMemo, useState } from 'react';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectMarketPricesBySymbol } from '../../../../Redux/Slices/MarketPricesSlice';
import useCurrencyConversionApi from '../../../Apis/Hooks/useCurrencyConversionApiHook';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import useUnitOfMeasureConversionApi from '../../../Apis/Hooks/useUnitOfMeasureConversionApiHook';
import PageLoadingSpinner from '../../../Components/LoadingSpinner/PageLoadingSpinner';
import useLightstreamerMarketPrices from '../../../Core/Hooks/useLightstreamerMarketPricesHook';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { SymbolContractCompositeModel } from '../../Futures/Prices/FuturesPricesTableDefinitions';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';
import { IDashboardPricesTableProps } from '../DashboardTables/DashboardTableFromPageElement';

const withFuturesPricesTableFromPageElement = (WrappedComponent: React.ComponentType<IDashboardPricesTableProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const [translations] = useLanguage();
        const { commodity, exchange, currency, unitOfMeasure, commodityDisplayName, subCommodityDisplayName } = props.pageElement;
        const marketPricesBySymbol = useApplicationSelector(selectMarketPricesBySymbol);
        const [symbolContractSelections, setSymbolContractSelections] = useState<SymbolContractCompositeModel[]>([]);

        const symbols = useSymbolsApi();

        const symbolModel = useMemo(() => {
            if (!symbols || !exchange || !commodity) {
                return undefined;
            }
            return symbols.find((x) => x.exchange === exchange && x.commodity === commodity);
        }, [symbols, exchange, commodity]);

        const [getUnitOfMeasureConversionRate] = useUnitOfMeasureConversionApi(symbolModel?.reutersInstrumentCodePrefix);
        const [getCurrencyConversionRate] = useCurrencyConversionApi(symbolModel?.reutersInstrumentCodePrefix);
        const { startSubscription } = useLightstreamerMarketPrices();

        useEffect(() => {
            if (!symbolModel?.symbolContracts) {
                return;
            }
            const codes = symbolModel.symbolContracts.map((c) => c.reutersInstrumentCode).filter(Boolean) as string[];
            startSubscription(codes);
        }, [symbolModel]);

        useEffect(() => {
            if (!symbolModel?.symbolContracts) {
                return;
            }

            const contracts = symbolModel.symbolContracts.map((row, index) => {
                const contract: SymbolContractCompositeModel = {
                    id: `${index}`,
                    rowIndex: index,
                    selected: true,
                    multipler: symbolModel.priceMultiplierFutures,
                    reutersInstrumentCodePrefix: symbolModel.reutersInstrumentCodePrefix,
                    ...row,
                };

                const marketPrice = marketPricesBySymbol[row.reutersInstrumentCode];

                if (!marketPrice) {
                    return contract;
                }

                return convertMarketPrice({
                    ...contract,
                    ...marketPrice,
                });
            });

            setSymbolContractSelections(contracts);
        }, [symbolModel, marketPricesBySymbol]);

        const convertMarketPrice = (contract: SymbolContractCompositeModel): SymbolContractCompositeModel => {
            const convert = (value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return getUnitOfMeasureConversionRate(unitOfMeasure) * getCurrencyConversionRate(currency, contract.year, contract.month) * value;
            };

            return {
                ...contract,
                ...{
                    contractYearMonth: `${contract.year}${contract.month}`,
                    latestPrice: contract.latestPrice ?? undefined,
                    latestPriceConverted: convert(contract.latestPrice),
                    latestOrSettledPriceConverted: convert(contract.latestPrice) ?? convert(contract.settlementPrice),
                    bidPriceConverted: convert(contract.bidPrice),
                    askPriceConverted: convert(contract.askPrice),
                    settlementPriceConverted: convert(contract.settlementPrice),
                    netChangeConverted: convert(contract.netChange),
                    openPriceConverted: convert(contract.openPrice),
                    expirationDateFormatted: formattingService.toShortDayMonthYear(contract.expirationDate),
                    lastSessionHighPriceConverted: convert(contract.lastSessionHighPrice),
                    lastSessionLowPriceConverted: convert(contract.lastSessionLowPrice),
                    settlementNetChangeConverted: convert(contract.settlementNetChange),
                    highPriceConverted: convert(contract.highPrice),
                    lowPriceConverted: convert(contract.lowPrice),
                    tradeRegistrationPriceConverted: convert(contract.tradeRegistrationPrice),
                    currency: currency ?? contract.currency,
                },
            };
        };

        if (!symbolContractSelections.length) {
            return <PageLoadingSpinner />;
        }

        const exchangePart = exchange && translations.exchange[exchange] ? translations.exchange[exchange] : exchange ?? '';
        const commodityPart = (subCommodityDisplayName ?? commodityDisplayName ?? '') as string;
        const futuresSuffix = translations.futures.titleWithoutOtc || 'Futures';
        const title = [exchangePart, commodityPart, futuresSuffix].filter(Boolean).join(' ');
        const customTitle = typeof props.customTitle === 'string' ? props.customTitle : undefined;

        return (
            <WrappedComponent
                tableType="futures"
                tableData={symbolContractSelections}
                title={title}
                customTitle={customTitle}
                dragIndicatorIcon={props.dragIndicatorIcon}
                isInDashboardEditMode={props.isInDashboardEditMode}
                maximizeMinimizeButton={props.maximizeMinimizeButton}
                useDynamicHeight={props.useDynamicHeight}
                handleMoreOptionsSelection={props.handleMoreOptionsSelection}
            />
        );
    };

    return Wrapped;
};

export default withFuturesPricesTableFromPageElement;
